import { getDirectRelationship } from '@cognite/apm-client';
import type {
  Condition,
  ConditionSourceViews,
  ConditionUpsertType,
  IAPMAppDataService,
  InFieldUser,
  ViewVersions,
} from '@cognite/apm-client';
import { type CogniteClientDependencies, FDMClient } from '@cognite/fdm-client';

import type { IConditionsService } from './conditions.service.interface';
import { getViewVersion } from './utils';

export type APMClientDependencies = {
  conditions: Pick<IAPMAppDataService<Condition>, 'upsert' | 'delete'>;
  appDataModelSpace: string;
  userInstanceSpace: string;
  viewVersions: Pick<ViewVersions, 'TEMPLATE_ITEM' | 'CHECKLIST_ITEM'>;
};

export class ConditionsService extends FDMClient implements IConditionsService {
  apmClient: APMClientDependencies;
  modelSpace: string;
  constructor(
    client: CogniteClientDependencies,
    apmClient: APMClientDependencies
  ) {
    super(client);
    this.apmClient = apmClient;
    this.modelSpace = apmClient.appDataModelSpace;
  }

  async createConditions(
    conditions: Condition[],
    user: InFieldUser,
    sourceView: ConditionSourceViews
  ) {
    const upsertedConditions: Condition[] = conditions.map((condition) => ({
      ...condition,
      createdBy: user,
      sourceView: `${this.modelSpace}/${sourceView}/${getViewVersion(
        sourceView,
        this.apmClient.viewVersions
      )}`,
    }));
    return this.upsertConditions(upsertedConditions).then(
      (result) => result.data.items[0]
    );
  }

  async updateConditions(conditions: Condition[], user: InFieldUser) {
    const upsertedConditions: Condition[] = conditions.map((condition) => ({
      ...condition,
      updatedBy: user,
    }));
    return this.upsertConditions(upsertedConditions);
  }

  async upsertConditions(conditions: Condition[]) {
    const upsertedConditions: ConditionUpsertType[] = conditions.map(
      (condition) => {
        const conditionalActionRelationship = getDirectRelationship(
          condition.conditionalAction
        );
        const sourceRelationship = getDirectRelationship(condition.source);
        const createdByRelationship = getDirectRelationship({
          externalId: condition.createdBy?.externalId,
          space: this.apmClient.userInstanceSpace,
        });
        const updatedByRelationship = getDirectRelationship({
          externalId: condition.updatedBy?.externalId,
          space: this.apmClient.userInstanceSpace,
        });
        return {
          ...condition,
          conditionalAction: conditionalActionRelationship,
          source: sourceRelationship,
          createdBy: createdByRelationship,
          updatedBy: updatedByRelationship,
        };
      }
    );
    return this.apmClient.conditions.upsert(upsertedConditions);
  }

  async deleteConditions(externalIds: string[]) {
    return this.apmClient.conditions.delete(externalIds);
  }
}

export const createConditionsService = (
  ...args: ConstructorParameters<typeof ConditionsService>
): IConditionsService => new ConditionsService(...args);
